<template>
  <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
    <div>
      <h3 class="text-slate font-semibold leading-6 text-gray-900">Send Proposal</h3>

      <div class="mt-4">
        <form @submit.stop.prevent="form.handleSubmit()">
          <form.Field
            name="to"
            :validators="{
              onChange: yup.array().of(yup.string().email('Invalid email address')).min(1, 'Email for is required')
            }"
          >
            <template #default="{ field }">
              <BaseTagsInput
                v-model="(field.state.value as string[])"
                label="Email for"
                placeholder="add email address"
                type="email"
                description="Enter new emails separated by space, comma or semicolon"
                :name="field.name"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @update:model-value="(value) => field.handleChange(value)"
              />
            </template>
          </form.Field>

          <form.Field
            name="cc"
            :validators="{
              onChange: yup.array().of(yup.string().email('Invalid email address'))
            }"
          >
            <template #default="{ field }">
              <BaseTagsInput
                v-model="(field.state.value as string[])"
                label="Cc"
                placeholder="add email address"
                type="email"
                description="Keep the realtor email if you want to send a copy to the realtor"
                :name="field.name"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @update:model-value="(value) => field.handleChange(value)"
              />
            </template>
          </form.Field>

          <form.Field
            name="message.subject"
            :validators="{
              onBlur: yup.string().required('Subject is required')
            }"
          >
            <template #default="{ field }">
              <BaseInput
                v-model="(field.state.value as string)"
                label="Subject"
                :name="field.name"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                placeholder="Subject"
                @blur="field.handleBlur"
                @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </form>

        <div v-html="previewHTML" />
      </div>
    </div>
  </div>

  <form.Subscribe>
    <template #default="{ isSubmitting, canSubmit }">
      <div class="bg-white py-3 flex px-6 justify-end gap-2">
        <BaseButton
          text="Cancel"
          :disabled="isSubmitting"
          :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
          @click="emit('close')"
        />

        <BaseButton
          variant="primary"
          :disabled="!canSubmit || isSubmitting"
          :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
          @click="form.handleSubmit()"
        >
          <div class="flex justify-between items-center gap-2">
            <span>Send</span>
            <span
              v-if="isSubmitting"
              class="i-heroicons-arrow-path animate-spin"
            />
          </div>
        </BaseButton>
      </div>
    </template>
  </form.Subscribe>

</template>

<script lang="ts" setup>
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTagsInput from '@/components/base/BaseTagsInput.vue'
import { Project, PROJECT_STATUS } from '@/models/project'
import FirebaseService from '@/services/FirebaseService'
import { Email } from '@/models/email'
import { Proposal, PROPOSAL_STATUS } from '@/models/proposal'
import BaseButton from '@/components/base/BaseButton.vue'
import { httpsCallable } from 'firebase/functions'
import { computed, PropType } from 'vue'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import ProposalService from '@/services/ProposalService'
import { useNotification } from '@kyvg/vue3-notification'

const props = defineProps({
  project: {
    type: Object as PropType<Project>,
    required: true
  },
  proposal: {
    type: Object as PropType<Proposal>,
    required: true
  },
  email: {
    type: Object as PropType<Email>,
    required: true
  }
})

const emit = defineEmits(['close'])

const form = useForm({
  defaultValues: props.email,
  validatorAdapter: yupValidator(),
  onSubmit: async () => {
    await sendEmail()
  }
})

const previewHTML = computed(() => props.email.message.html)
const proposalService = new ProposalService()
const queryClient = useQueryClient()
const { notify } = useNotification()

const { mutateAsync: sendEmail } = useMutation({
  mutationFn: async () => {
    const functions = await FirebaseService.functions()
    const send = httpsCallable(functions, 'email-sendV2')
    await send(props.email)

    // project status will be updated when proposal status is updated
    if(props.proposal.status === PROPOSAL_STATUS.DRAFT) {
      const updatedProposal = await proposalService.update(props.proposal.id, { ...props.proposal, status: PROPOSAL_STATUS.AWAITING_ACCEPTANCE})
      queryClient.setQueryData(['proposal', updatedProposal.id], updatedProposal)
      queryClient.setQueryData(['project', updatedProposal.id], {...props.project, status: PROJECT_STATUS.AWAITING_ACCEPTANCE })
    }
  },
  onSuccess: () => {
    emit('close')
    notify({
      type: 'success',
      title: 'Email sucessfully sent!'
    })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error.',
      text: error.message || 'Error sending email.'
    })
  }
})

</script>
